import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Copy, Sparkles } from "lucide-react";

// ================== Contenu réseaux sociaux (IA) ==================
export function AdminSocial() {
  const [platform, setPlatform] = useState("instagram");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("chaleureux, littéraire, cocon");
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/admin/social/history").then(r => setHistory(r.data.items || []));
  useEffect(() => { loadHistory(); }, []);

  async function generate(e) {
    e.preventDefault();
    if (!topic.trim()) return toast.error("Merci d'indiquer un sujet");
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/admin/social/generate", { platform, topic, tone, count: parseInt(count, 10) });
      setResult(r.data);
      loadHistory();
      toast.success("Contenu généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération");
    } finally { setLoading(false); }
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Copié");
  }

  return (
    <div data-testid="admin-social">
      <div className="mb-8">
        <p className="font-hand text-xl text-[#A3876A]">Contenu réseaux sociaux</p>
        <h1 className="font-serif text-4xl">Générateur IA — Instagram & Pinterest</h1>
      </div>

      <form onSubmit={generate} className="bg-[#F5F0E6] p-8 border border-[#E8DCC8] mb-10 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Plateforme</label>
            <select data-testid="social-platform" value={platform} onChange={e=>setPlatform(e.target.value)}
              className="w-full bg-transparent border-b border-[#E8DCC8] py-2 outline-none">
              <option value="instagram">Instagram</option>
              <option value="pinterest">Pinterest</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Nombre de propositions</label>
            <input data-testid="social-count" type="number" min="1" max="8" value={count} onChange={e=>setCount(e.target.value)} className="field-input"/>
          </div>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Sujet / thème</label>
          <input data-testid="social-topic" required value={topic} onChange={e=>setTopic(e.target.value)}
            placeholder="Ex : L'écriture comme rituel du soir, journaling en hiver, une lettre à soi-même…"
            className="field-input"/>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Ton souhaité</label>
          <input data-testid="social-tone" value={tone} onChange={e=>setTone(e.target.value)} className="field-input"/>
        </div>
        <button data-testid="social-generate" disabled={loading} className="btn-primary inline-flex items-center gap-2">
          <Sparkles size={14}/> {loading ? "Génération…" : "Générer"}
        </button>
      </form>

      {result && (
        <div data-testid="social-results" className="mb-10 space-y-4">
          <p className="font-hand text-xl text-[#A3876A]">Résultats — {result.platform}</p>
          {(result.posts || []).map((p, i) => (
            <article key={i} className="bg-[#FDFBF7] border border-[#E8DCC8] p-6">
              {p.title && <h3 className="font-serif text-2xl mb-3">{p.title}</h3>}
              <p className="text-[#4A4036] leading-relaxed whitespace-pre-wrap">{p.body}</p>
              {p.hashtags && p.hashtags.length > 0 && (
                <p className="mt-4 text-sm text-[#A3876A]">{p.hashtags.join(" ")}</p>
              )}
              <button onClick={()=>copy(`${p.title ? p.title+"\n\n" : ""}${p.body}${p.hashtags?.length ? "\n\n"+p.hashtags.join(" ") : ""}`)}
                className="mt-4 inline-flex items-center gap-1 text-xs text-[#A3876A] hover:underline">
                <Copy size={12}/> Copier
              </button>
            </article>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <p className="font-hand text-xl text-[#A3876A] mb-3">Historique récent</p>
          <ul className="text-sm text-[#4A4036] space-y-2">
            {history.slice(0, 10).map(h => (
              <li key={h.generation_id} className="border-b border-[#E8DCC8] pb-2">
                <span className="text-xs text-[#7C7064]">{new Date(h.created_at).toLocaleString("fr-FR")}</span>
                {" — "}<em>{h.platform}</em> · {h.topic}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ================== Blog / Journal ==================
export function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // slug or "__new__"
  const [f, setF] = useState({ slug: "", title: "", excerpt: "", content: "", cover_image: "", tags: [], published: false });
  const load = () => api.get("/admin/blog").then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  function openNew() {
    setF({ slug: "", title: "", excerpt: "", content: "", cover_image: "", tags: [], published: false });
    setEditing("__new__");
  }
  function openEdit(art) {
    setF({ slug: art.slug, title: art.title, excerpt: art.excerpt || "", content: art.content || "",
           cover_image: art.cover_image || "", tags: art.tags || [], published: !!art.published });
    setEditing(art.slug);
  }
  async function save() {
    if (!f.slug || !f.title || !f.content) return toast.error("Slug, titre et contenu requis");
    try {
      if (editing === "__new__") await api.post("/admin/blog", f);
      else await api.put(`/admin/blog/${editing}`, f);
      toast.success("Enregistré"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }
  async function remove(slug) {
    if (!window.confirm(`Supprimer "${slug}" ?`)) return;
    try { await api.delete(`/admin/blog/${slug}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  }

  if (editing) return (
    <div data-testid="admin-blog-edit" className="max-w-4xl">
      <p className="font-hand text-xl text-[#A3876A] mb-2">{editing === "__new__" ? "nouvel article" : `édition : ${editing}`}</p>
      <h1 className="font-serif text-4xl mb-8">Article de journal</h1>
      <div className="space-y-6 bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Slug (URL)</label>
            <input data-testid="blog-slug" className="field-input" placeholder="ex: ecrire-en-hiver" value={f.slug} onChange={e=>setF({...f, slug: e.target.value.toLowerCase().replace(/\s+/g,"-")})} disabled={editing !== "__new__"}/>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Image de couverture (URL)</label>
            <input data-testid="blog-cover" className="field-input" placeholder="https://…" value={f.cover_image} onChange={e=>setF({...f, cover_image: e.target.value})}/>
          </div>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Titre</label>
          <input data-testid="blog-title" className="field-input" value={f.title} onChange={e=>setF({...f, title: e.target.value})}/>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Chapô (accroche courte)</label>
          <Textarea data-testid="blog-excerpt" rows={2} value={f.excerpt} onChange={e=>setF({...f, excerpt: e.target.value})} className="bg-white border border-[#E8DCC8]"/>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Contenu (Markdown ou HTML léger)</label>
          <Textarea data-testid="blog-content" rows={16} value={f.content} onChange={e=>setF({...f, content: e.target.value})} className="font-mono text-sm bg-white border border-[#E8DCC8]"/>
          <p className="text-xs text-[#7C7064] mt-1">Sauts de ligne = paragraphes. Vous pouvez utiliser <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;a&gt;</code>.</p>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Tags (séparés par virgule)</label>
          <input className="field-input" value={(f.tags || []).join(", ")} onChange={e=>setF({...f, tags: e.target.value.split(",").map(t=>t.trim()).filter(Boolean)})}/>
        </div>
        <label className="flex items-center gap-2">
          <input data-testid="blog-published" type="checkbox" checked={f.published} onChange={e=>setF({...f, published: e.target.checked})}/>
          <span>Publié (visible publiquement)</span>
        </label>
        <div className="flex gap-3">
          <button data-testid="blog-save" onClick={save} className="btn-primary">Enregistrer</button>
          <button onClick={()=>setEditing(null)} className="btn-outline">Annuler</button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Journal / blog</p>
          <h1 className="font-serif text-4xl">Articles</h1>
        </div>
        <button data-testid="blog-new" onClick={openNew} className="btn-primary">Nouvel article</button>
      </div>
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Titre</th><th className="p-3">Slug</th><th className="p-3">État</th><th className="p-3">Créé</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#7C7064]">Aucun article. Créez votre premier récit ✦</td></tr>}
            {items.map(a => (
              <tr key={a.slug} className="border-t border-[#E8DCC8]">
                <td className="p-3">{a.title}</td>
                <td className="p-3 font-mono text-xs">{a.slug}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs ${a.published ? "bg-[#7B8A72] text-white" : "bg-[#E8DCC8]"}`}>{a.published ? "publié" : "brouillon"}</span></td>
                <td className="p-3 text-xs text-[#7C7064]">{new Date(a.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 space-x-3">
                  <button data-testid={`blog-edit-${a.slug}`} onClick={()=>openEdit(a)} className="text-xs underline text-[#A3876A]">Modifier</button>
                  <button data-testid={`blog-delete-${a.slug}`} onClick={()=>remove(a.slug)} className="text-xs text-[#A86962] hover:underline">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================== Témoignages ==================
export function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", quote: "", context: "", photo_url: "", display_order: 100, published: true });
  const load = () => api.get("/admin/testimonials").then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  function openNew() {
    setF({ name: "", quote: "", context: "", photo_url: "", display_order: (items.length + 1) * 10, published: true });
    setEditing("__new__");
  }
  function openEdit(t) {
    setF({ name: t.name, quote: t.quote, context: t.context || "", photo_url: t.photo_url || "",
           display_order: t.display_order || 100, published: !!t.published });
    setEditing(t.testimonial_id);
  }
  async function save() {
    if (!f.name || !f.quote) return toast.error("Nom et citation requis");
    try {
      if (editing === "__new__") await api.post("/admin/testimonials", f);
      else await api.put(`/admin/testimonials/${editing}`, f);
      toast.success("Enregistré"); setEditing(null); load();
    } catch { toast.error("Erreur"); }
  }
  async function remove(id) {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  }

  if (editing) return (
    <div data-testid="admin-testimonials-edit" className="max-w-2xl">
      <h1 className="font-serif text-4xl mb-8">{editing === "__new__" ? "Nouveau témoignage" : "Éditer un témoignage"}</h1>
      <div className="space-y-6 bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Prénom / nom de la cliente</label>
          <input data-testid="testimonial-name" className="field-input" value={f.name} onChange={e=>setF({...f, name: e.target.value})}/>
        </div>
        <div>
          <label className="text-sm text-[#7C7064] block mb-1">Citation</label>
          <Textarea data-testid="testimonial-quote" rows={4} value={f.quote} onChange={e=>setF({...f, quote: e.target.value})} className="bg-white border border-[#E8DCC8]"/>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Contexte (ex : Programme Essentiel)</label>
            <input className="field-input" value={f.context} onChange={e=>setF({...f, context: e.target.value})}/>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Photo URL (facultatif)</label>
            <input className="field-input" placeholder="https://…" value={f.photo_url} onChange={e=>setF({...f, photo_url: e.target.value})}/>
          </div>
          <div>
            <label className="text-sm text-[#7C7064] block mb-1">Ordre d'affichage (asc)</label>
            <input type="number" className="field-input" value={f.display_order} onChange={e=>setF({...f, display_order: parseInt(e.target.value, 10) || 0})}/>
          </div>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={f.published} onChange={e=>setF({...f, published: e.target.checked})}/>
            <span>Publié</span>
          </label>
        </div>
        <div className="flex gap-3">
          <button data-testid="testimonial-save" onClick={save} className="btn-primary">Enregistrer</button>
          <button onClick={()=>setEditing(null)} className="btn-outline">Annuler</button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl">Témoignages</h1>
        <button data-testid="testimonial-new" onClick={openNew} className="btn-primary">Nouveau témoignage</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-[#7C7064]">Aucun témoignage. Ajoutez la première voix qui parle de vous ✦</p>}
        {items.map(t => (
          <div key={t.testimonial_id} className="bg-[#FDFBF7] border border-[#E8DCC8] p-6">
            <p className="font-serif text-lg italic mb-3">« {t.quote} »</p>
            <p className="font-hand text-lg text-[#A3876A]">— {t.name}</p>
            {t.context && <p className="text-xs text-[#7C7064]">{t.context}</p>}
            <div className="flex items-center justify-between mt-4">
              <span className={`text-xs px-2 py-0.5 ${t.published ? "bg-[#7B8A72] text-white" : "bg-[#E8DCC8]"}`}>{t.published ? "publié" : "brouillon"}</span>
              <div className="space-x-3">
                <button onClick={()=>openEdit(t)} className="text-xs underline text-[#A3876A]">Modifier</button>
                <button onClick={()=>remove(t.testimonial_id)} className="text-xs text-[#A86962] hover:underline">Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ================== Leads (aimants) ==================
export function AdminLeads() {
  const [data, setData] = useState({ items: [], count: 0 });
  useEffect(() => { api.get("/admin/leads").then(r => setData(r.data)); }, []);
  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-hand text-xl text-[#A3876A]">Aimants collectés</p>
          <h1 className="font-serif text-4xl">Leads</h1>
        </div>
        <p className="font-serif text-3xl">{data.count}</p>
      </div>
      <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F0E6] text-left"><tr>
            <th className="p-3">Date</th><th className="p-3">Email</th><th className="p-3">Nom</th><th className="p-3">Source</th>
          </tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-[#7C7064]">Aucun lead collecté pour l'instant.</td></tr>}
            {data.items.map(l => (
              <tr key={l.email} className="border-t border-[#E8DCC8]">
                <td className="p-3 text-xs text-[#7C7064]">{new Date(l.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3">{l.email}</td>
                <td className="p-3">{l.name || "—"}</td>
                <td className="p-3 text-xs">{l.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
